import type { AuthProvider } from "@refinedev/core";
import { resolveApiUrl } from "./api-url";
import { loadPermissions, clearPermissionsCache } from "@/lib/permissions-cache";
import {
  clearActiveTenant,
  getActiveTenant,
  getPreferredTenant,
  seedActiveTenantFromToken,
} from "@/lib/active-tenant";

const API_URL = resolveApiUrl();

const AUTH_DEBUG = import.meta.env.DEV;

type AuthLogPayload = Record<string, string | number | boolean | null>;

function logAuthEvent(
  level: "info" | "warn" | "error",
  message: string,
  payload?: AuthLogPayload
): void {
  if (!AUTH_DEBUG) {
    return;
  }

  const details = payload ? ` ${JSON.stringify(payload)}` : "";
  console[level](`[auth] ${message}${details}`);
}

/**
 * Response format from auth endpoints
 */
interface AuthAccessToken {
  accessToken: string;
  expiresIn: number;
}

interface UserIdentity {
  id: number;
  email: string;
  role: string;
  permissions?: string[];
}

interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  errors: string[] | null;
}

function normalizeRedirectTo(redirectTo?: string | null): string {
  if (!redirectTo) {
    return "/";
  }

  if (!redirectTo.startsWith("/") || redirectTo.startsWith("//")) {
    return "/";
  }

  if (redirectTo.startsWith("/login")) {
    return "/";
  }

  return redirectTo;
}

function buildLoginRedirectPath(): string {
  if (typeof window === "undefined") {
    return "/login";
  }

  const { pathname, search, hash } = window.location;

  if (pathname.startsWith("/login")) {
    return "/login";
  }

  const currentPath = `${pathname}${search}${hash}`;
  return `/login?to=${encodeURIComponent(currentPath)}`;
}

/**
 * Refresh access token using refresh token
 */
let refreshPromise: Promise<boolean> | null = null;

export async function refreshTokens(): Promise<boolean> {
  if (refreshPromise) {
    return refreshPromise;
  }

  const accessTokenAtStart = localStorage.getItem("accessToken");

  refreshPromise = (async () => {
    try {
      logAuthEvent("info", "refresh:start");
      // refresh-cookie не несёт тенанта, а access-токен мог протухнуть — иначе
      // backend резолвит default-тенанта и ищет refresh-токен не в той БД (401).
      // Берём активный slug из JWT, фолбэк — preferred из localStorage.
      const tenant = getActiveTenant() ?? getPreferredTenant();
      const response = await fetch(`${API_URL}/auth/admin/refresh`, {
        method: "POST",
        credentials: "include",
        headers: tenant ? { "X-Tenant-Id": tenant } : undefined,
      });
      
      if (!response.ok) {
        logAuthEvent("warn", "refresh:response", { status: response.status, ok: response.ok });
        const currentToken = localStorage.getItem("accessToken");
        if (!currentToken || currentToken === accessTokenAtStart) {
          localStorage.removeItem("accessToken");
        }
        localStorage.removeItem("refreshToken");
        return false;
      }
      
      const json: ApiResponse<AuthAccessToken> = await response.json();
      
      if (!json.success || !json.data) {
        logAuthEvent("warn", "refresh:payload_invalid", { success: json.success });
        const currentToken = localStorage.getItem("accessToken");
        if (!currentToken || currentToken === accessTokenAtStart) {
          localStorage.removeItem("accessToken");
        }
        localStorage.removeItem("refreshToken");
        return false;
      }
      
      localStorage.setItem("accessToken", json.data.accessToken);
      localStorage.removeItem("refreshToken");
      seedActiveTenantFromToken();
      logAuthEvent("info", "refresh:success");
      return true;
    } catch {
      logAuthEvent("error", "refresh:error");
      const currentToken = localStorage.getItem("accessToken");
      if (!currentToken || currentToken === accessTokenAtStart) {
        localStorage.removeItem("accessToken");
      }
      localStorage.removeItem("refreshToken");
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Variable to store the pending /me request promise to prevent duplicates
 */
let meRequestPromise: Promise<UserIdentity | null> | null = null;
let meRequestTimestamp: number = 0;
let meRequestCache: UserIdentity | null = null;

// Cache duration in milliseconds (e.g., 2 seconds)
const ME_CACHE_DURATION = 2000;

/**
 * Очистить кэш /me (вызывается при logout)
 */
export function clearMeCache(): void {
  meRequestCache = null;
  meRequestTimestamp = 0;
  meRequestPromise = null;
}

/**
 * Fetch current user identity with deduplication and short caching.
 *
 * НЕ обрабатывает 401 самостоятельно — это делает fetch interceptor
 * (добавляет Authorization header, делает refresh + retry при 401).
 */
async function fetchMe(): Promise<UserIdentity | null> {
  const now = Date.now();

  // Return cached result if fresh
  if (meRequestCache && (now - meRequestTimestamp < ME_CACHE_DURATION)) {
    logAuthEvent("info", "me:cache_hit");
    return meRequestCache;
  }

  // If a request is already in flight, return its promise
  if (meRequestPromise) {
    return meRequestPromise;
  }

  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken) {
    logAuthEvent("warn", "me:no_access_token");
    return null;
  }

  // Create new request promise
  // Interceptor автоматически добавит Authorization header и обработает 401
  meRequestPromise = (async () => {
    try {
      logAuthEvent("info", "me:request");
      const response = await fetch(`${API_URL}/auth/admin/me`);

      if (!response.ok) {
        logAuthEvent("warn", "me:response", { status: response.status });
        return null;
      }

      const json: ApiResponse<UserIdentity> = await response.json();
      return json.success && json.data ? json.data : null;
    } catch {
      logAuthEvent("error", "me:error");
      return null;
    } finally {
       meRequestPromise = null;
    }
  })();

  // Wait for result and update cache
  const result = await meRequestPromise;
  if (result) {
    meRequestCache = result;
    meRequestTimestamp = Date.now();
    logAuthEvent("info", "me:success");
  } else {
    logAuthEvent("warn", "me:empty");
  }
  return result;
}

/**
 * AuthProvider for Masters Club Admin Panel
 * Handles JWT authentication with access/refresh token rotation
 */
export const authProvider: AuthProvider = {
  /**
   * Login with email and password
   */
  login: async ({ email, password, redirectTo }) => {
    try {
      logAuthEvent("info", "login:start", { hasRedirectTo: Boolean(redirectTo) });
      const preferredTenant = getPreferredTenant();
      const response = await fetch(`${API_URL}/auth/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          preferredTenant
            ? { email, password, preferredTenant }
            : { email, password },
        ),
        credentials: "include",
      });
      
      const json: ApiResponse<AuthAccessToken> = await response.json();
      
      if (!json.success || !json.data) {
        logAuthEvent("warn", "login:failed", { ok: response.ok, status: response.status });
        return {
          success: false,
          error: {
            name: "Ошибка авторизации",
            message: json.errors?.join("\n") ?? "Неверный email или пароль",
          },
        };
      }
      
      localStorage.setItem("accessToken", json.data.accessToken);
      localStorage.removeItem("refreshToken");
      // Сразу сидируем X-Tenant-Id из JWT: иначе check()/fetchMe уйдут без
      // заголовка в default-БД, а sub из non-default тенанта даст 404.
      seedActiveTenantFromToken();
      logAuthEvent("info", "login:success", { status: response.status });
      
      return {
        success: true,
        redirectTo: normalizeRedirectTo(redirectTo),
      };
    } catch (error) {
      logAuthEvent("error", "login:error", { message: error instanceof Error ? error.message : "unknown" });
      return {
        success: false,
        error: {
          name: "Ошибка авторизации",
          message: error instanceof Error ? error.message : "Ошибка подключения к серверу",
        },
      };
    }
  },

  /**
   * Logout and invalidate refresh token
   */
  logout: async () => {
    try {
      // Тот же tenant-контекст, что при refresh — иначе logout удалит токен из
      // default-БД, а живой refresh-токен останется в БД активного тенанта.
      const tenant = getActiveTenant() ?? getPreferredTenant();
      await fetch(`${API_URL}/auth/admin/logout`, {
        method: "POST",
        credentials: "include",
        headers: tenant ? { "X-Tenant-Id": tenant } : undefined,
      });
    } catch {
      // Ignore logout errors
    }

    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    clearPermissionsCache();
    clearMeCache();
    clearActiveTenant();

    return {
      success: true,
      redirectTo: "/login",
    };
  },

  /**
   * Check if user is authenticated
   */
  check: async () => {
    const accessToken = localStorage.getItem("accessToken");
    
    if (!accessToken) {
      logAuthEvent("warn", "check:no_access_token");
      const refreshed = await refreshTokens();
      if (refreshed) {
        logAuthEvent("info", "check:authenticated_after_refresh");
        return { authenticated: true };
      }
      return {
        authenticated: false,
        redirectTo: buildLoginRedirectPath(),
      };
    }
    
    // optimize: use shared fetchMe
    const identity = await fetchMe();

    if (identity) {
      logAuthEvent("info", "check:authenticated");
      return { authenticated: true };
    }

    // fetchMe naturally handles refresh logic internally now, but returns null if failed
    // If fetchMe returned null, we assume auth failed even after refresh attempts
    return {
      authenticated: false,
      redirectTo: buildLoginRedirectPath(),
      logout: true,
    };
  },

  /**
   * Get current user identity
   */
  getIdentity: async () => {
    const identity = await fetchMe();
    
    if (!identity) {
      return null;
    }
    
    return {
      id: identity.id,
      name: identity.email,
      email: identity.email,
      role: identity.role,
    };
  },

  /**
   * Get user permissions for access control
   * Использует централизованный кэш прав
   */
  getPermissions: async () => {
    return loadPermissions();
  },

  /**
   * Handle authentication errors (401, 403)
   *
   * ВАЖНО: 401 обработка (refresh + retry) происходит в fetch interceptor.
   * Если ошибка 401 дошла сюда — значит interceptor уже попробовал refresh и он не удался.
   * Поэтому здесь просто сигнализируем logout.
   */
  onError: async (error) => {
    const status = error?.status ?? error?.statusCode;

    if (status === 401) {
      return {
        logout: true,
        redirectTo: buildLoginRedirectPath(),
        error: {
          name: "Сессия истекла",
          message: "Сессия истекла. Войдите снова.",
        },
      };
    }

    if (status === 403) {
      return {
        redirectTo: "/forbidden",
        error: {
          name: "Доступ запрещен",
          message: "У вас недостаточно прав для этого действия",
        },
      };
    }

    return {};
  },

  /**
   * Register new user (not used in admin panel)
   */
  register: async () => {
    return {
      success: false,
      error: {
        name: "Функция недоступна",
        message: "Регистрация недоступна",
      },
    };
  },

  /**
   * Forgot password (not implemented yet)
   */
  forgotPassword: async () => {
    return {
      success: false,
      error: {
        name: "Функция недоступна",
        message: "Восстановление пароля недоступно",
      },
    };
  },

  /**
   * Update password
   */
  updatePassword: async ({ currentPassword, newPassword }) => {
    const accessToken = localStorage.getItem("accessToken");
    
    if (!accessToken) {
      return {
        success: false,
        error: {
          name: "Ошибка авторизации",
          message: "Необходима авторизация",
        },
      };
    }
    
    try {
      const response = await fetch(`${API_URL}/auth/admin/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      
      const json: ApiResponse<{ message: string }> = await response.json();
      
      if (!json.success) {
        return {
          success: false,
          error: {
            name: "Ошибка смены пароля",
            message: json.errors?.join("\n") ?? "Ошибка смены пароля",
          },
        };
      }
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: {
          name: "Ошибка смены пароля",
          message: error instanceof Error ? error.message : "Ошибка смены пароля",
        },
      };
    }
  },
};
